import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/base/SeoHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { OrganizationSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import { testimonials, stats } from '@/mocks/resources';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormError('');
      return;
    }

    setFormSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9teu1v8l97n3eas38fg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试。');
          setFormSubmitted(false);
        } else {
          setFormError(serverMsg || '提交失败，请稍后再试。');
          setFormSubmitted(false);
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormSubmitted(false);
    } finally {
      setFormSubmitting(false);
    }
  };

  return (
    <Layout>
      <SeoHead
        title="关于我们 - 无码专区"
        description="了解无码专区品牌故事与发展历程。无码专区是专为内容爱好者打造的精选资源聚合平台，汇聚海量高清无水印素材。查看品牌介绍、用户口碑及联系方式。"
        keywords="无码专区关于我们,无码专区品牌介绍,无码专区联系方式,无码专区用户口碑,无码专区资源平台"
        canonicalUrl="/about"
      />
      <OrganizationSchema />
      <WebPageSchema
        name="关于我们 - 无码专区"
        description="了解无码专区品牌故事、发展历程与联系方式"
        url="https://aialhku.cn/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: 'https://aialhku.cn/' },
          { name: '关于我们', item: 'https://aialhku.cn/about' },
        ]}
      />

      <div className="pt-20 pb-14 md:pb-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <div className="mt-6 text-center mb-14">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              关于无码专区
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              我们致力于为设计师和创作者打造一个开放、优质、免费的资源共享平台
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div>
              <div className="w-full h-64 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20designers%20collaborating%20on%20digital%20projects%20warm%20ambient%20lighting%20clean%20minimal%20office%20with%20large%20monitors%20showing%20design%20work%20plants%20and%20natural%20light%20professional%20creative%20studio%20atmosphere&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="无码专区品牌故事"
                  title="无码专区 - 品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                品牌故事
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                无码专区创立于2023年，由一群热爱设计、热爱分享的创作者共同发起。我们深知创作者在项目初期面临的资源困境——优质素材价格高昂、免费资源质量参差不齐。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                怀揣着"让每一个创作者都能用上好资源"的初心，我们搭建了这个完全免费的资源聚合平台。截至目前，平台已收录超过{stats.totalResources.toLocaleString()}个优质资源，服务了{stats.totalUsers.toLocaleString()}+活跃用户。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们相信，好的创作工具和资源不应该成为创作的阻碍。无码专区将持续为您提供高质量、多样化的创作资源，陪伴每一位创作者成长。
              </p>
            </div>
          </div>

          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500">
                听听创作者们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">{t.content}</p>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div id="contact" className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/70">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  有任何问题、建议或合作意向？欢迎通过以下方式与我们取得联系，我们会在24小时内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-500">contact@aialhku.cn</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-50 flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">微信公众号</div>
                      <div className="text-sm text-foreground-500">无码专区</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                      <i className="ri-time-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">工作时间</div>
                      <div className="text-sm text-foreground-500">周一至周五 09:00 - 18:00</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formSubmitted ? (
                  <div className="flex flex-col items-center justify-center h-full py-10">
                    <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                      <i className="ri-check-line text-3xl text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">提交成功！</h3>
                    <p className="text-sm text-foreground-500 text-center">感谢您的留言，我们会尽快回复。</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    id="contact-form"
                    className="space-y-4"
                    noValidate
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容，不超过500字"
                      ></textarea>
                    </div>

                    <div className="honeypot-field">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {formError && (
                      <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formSubmitting}
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                    >
                      {formSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>发送留言
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}